import React, { Suspense, lazy, useContext } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { StoreContext } from '../contexts/StoreContext';

/**
 * Генератор роутов из routes.model.json
 * Создает Routes структуру на основе модели с проверкой доступов
 */
const RouteGenerator = observer(() => {
    const rootStore = useContext(StoreContext);
    const { models, auth } = rootStore;
    
    const userRole = auth.userRole;
    const routesModel = models?.schemas?.routes;
    
    if (!routesModel || !routesModel.routes) {
        // Фоллбек на хардкод роуты если модель не загружена
        console.warn('[RouteGenerator] Routes model not loaded, using fallback');
        return (
            <Routes>
                <Route path="/" element={<div>Loading...</div>} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        );
    }
    
    // Рекурсивная генерация роутов
    const generateRoutes = (routes, isNested = false) => {
        const routeElements = [];
        
        for (const [path, config] of Object.entries(routes)) {
            // Убираем начальный слеш для вложенных роутов
            const cleanPath = isNested ? path.replace(/^\//, '') : path;
            
            // Проверяем доступ
            if (!hasAccess(config, userRole)) {
                continue;
            }
            
            // Динамический импорт компонента
            const Component = loadComponent(config.component);
            
            // Обработка редиректа
            if (config.redirect) {
                routeElements.push(
                    <Route 
                        key={cleanPath}
                        path={cleanPath}
                        element={<Navigate to={config.redirect} replace />}
                    />
                );
            }
            
            // Создаем роут
            if (config.children) {
                // Роут с дочерними элементами
                routeElements.push(
                    <Route 
                        key={cleanPath}
                        path={cleanPath}
                        element={<Component />}
                    >
                        {generateRoutes(config.children, true)}
                        {config.redirect && (
                            <Route index element={<Navigate to={config.redirect} replace />} />
                        )}
                    </Route>
                );
            } else if (Component) {
                // Простой роут
                routeElements.push(
                    <Route 
                        key={cleanPath}
                        path={cleanPath}
                        element={<Component />}
                    />
                );
            }
        }
        
        return routeElements;
    };
    
    // Проверка доступа к роуту
    const hasAccess = (config, role) => {
        if (!config.access) return false;
        if (!config.access.roles) return false;
        return config.access.roles.includes(role);
    };
    
    // Динамическая загрузка компонента
    const loadComponent = (componentName) => {
        if (!componentName) return null;
        
        // Кеш загруженных компонентов
        if (!window._routeComponents) {
            window._routeComponents = {};
        }
        
        if (!window._routeComponents[componentName]) {
            try {
                // Динамический импорт из папки pages
                window._routeComponents[componentName] = lazy(() =>
                    import(`../pages/${componentName}.jsx`)
                        .catch(() => import(`../pages/${componentName}/index.jsx`))
                        .catch(() => import(`../pages/${componentName}/${componentName}.jsx`))
                        .catch(() => {
                            // Попробуем найти в подпапках по префиксу
                            // Например: SettingsBase -> Settings/SettingsBase.jsx
                            const prefixMatch = componentName.match(/^([A-Z][a-z]+)/);
                            if (prefixMatch) {
                                const prefix = prefixMatch[1];
                                return import(`../pages/${prefix}/${componentName}.jsx`);
                            }
                            throw new Error(`Component ${componentName} not found`);
                        })
                        .catch(() => {
                            // Специальная проверка для DocxTemplateEditor в Settings
                            if (componentName === 'DocxTemplateEditor') {
                                return import(`../pages/Settings/DocxTemplateEditor.jsx`);
                            }
                            throw new Error(`Component ${componentName} not found`);
                        })
                );
            } catch (error) {
                console.error(`[RouteGenerator] Failed to load component ${componentName}:`, error);
                return () => <div>Component {componentName} not found</div>;
            }
        }
        
        return window._routeComponents[componentName];
    };
    
    // Добавляем тестовые роуты
    const TestEditor = lazy(() => import('../pages/TestEditor.jsx'));

    return (
        <Suspense fallback={<div>Loading...</div>}>
            <Routes>
                {generateRoutes(routesModel.routes)}
                {/* Временный тестовый роут */}
                <Route path="/test-editor" element={<TestEditor />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    );
});

export default RouteGenerator;